import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { Button } from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { GoogleSignInButton } from '@/components/molecules/GoogleSignInButton';

export type LoginCardProps = {
  email: string;
  onEmailChange: (value: string) => void;
  otp: string;
  onOtpChange: (value: string) => void;
  otpSent: boolean;
  otpTimeout: number;
  otpResendCount: number;
  isSendingOtp: boolean;
  isVerifyingOtp: boolean;
  isGoogleSigningIn: boolean;
  onSendOtp: () => void;
  onResendOtp: () => void;
  onVerifyOtp: () => void;
  onChangeEmail: () => void;
  onGoogleSignIn: () => void;
  onContactAdmin: () => void;
};

export function LoginCard({
  email,
  onEmailChange,
  otp,
  onOtpChange,
  otpSent,
  otpTimeout,
  otpResendCount,
  isSendingOtp,
  isVerifyingOtp,
  isGoogleSigningIn,
  onSendOtp,
  onResendOtp,
  onVerifyOtp,
  onChangeEmail,
  onGoogleSignIn,
  onContactAdmin,
}: LoginCardProps) {
  const canResend = otpTimeout === 0 && otpResendCount > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 423,
        bgcolor: '#fff',
        borderRadius: '10px',
        boxShadow: '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
        px: 5,
        pt: 3.625,
        pb: 3,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 20,
          lineHeight: '37px',
          textAlign: 'center',
          color: '#1e2a39',
          mb: 0,
        }}
      >
        Welcome back.
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 11,
          lineHeight: '37px',
          textAlign: 'center',
          color: '#1e2a39',
          mt: 0.5,
          mb: 3,
          px: 0.5,
        }}
      >
        Log in to continue building and managing your FF&E proposals.
      </Typography>

      <GoogleSignInButton
        fullWidth
        disabled={isGoogleSigningIn}
        label={isGoogleSigningIn ? 'Redirecting to Google...' : 'Sign in with Google'}
        onClick={onGoogleSignIn}
        sx={{ mb: 2 }}
      />

      <FormField
        label="Email"
        id="login-email"
        textFieldProps={{
          type: 'email',
          name: 'email',
          autoComplete: 'email',
          value: email,
          disabled: otpSent,
          onChange: e => onEmailChange(e.target.value),
          placeholder: 'you@company.com',
          InputProps: otpSent
            ? {
                endAdornment: (
                  <Typography
                    component="span"
                    onClick={onChangeEmail}
                    sx={{
                      fontSize: 12,
                      color: '#000',
                      width: 120,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Change Email
                  </Typography>
                ),
              }
            : undefined,
        }}
      />

      {otpSent && (
        <Box sx={{ mt: 2 }}>
          <FormField
            label="OTP"
            id="login-otp"
            textFieldProps={{
              type: 'text',
              name: 'otp',
              autoComplete: 'one-time-code',
              value: otp,
              onChange: e => onOtpChange(e.target.value),
              placeholder: '------',
              inputProps: { maxLength: 6 },
            }}
          />
        </Box>
      )}

      <Button
        fullWidth
        loading={isSendingOtp || isVerifyingOtp}
        onClick={otpSent ? onVerifyOtp : onSendOtp}
        sx={{ mt: 2 }}
      >
        {otpSent ? 'Submit' : 'Send OTP'}
      </Button>

      {otpSent && otpResendCount > 0 && (
        <Box
          onClick={canResend ? onResendOtp : undefined}
          sx={{
            mt: 1.5,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 400,
            cursor: canResend ? 'pointer' : 'default',
            color: '#1E2A39',
          }}
        >
          Resend{otpTimeout > 0 ? ` in ${otpTimeout}s` : ''}
        </Box>
      )}

      <Typography
        component="p"
        sx={{
          textAlign: 'center',
          mt: 2,
          mb: 0,
          fontSize: 11,
          lineHeight: '23.375px',
          color: '#1e2a39',
        }}
      >
        Don&apos;t have access?{' '}
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onContactAdmin}
          underline="hover"
          sx={{
            cursor: 'pointer',
            fontSize: 11,
            lineHeight: '23.375px',
            color: '#4a5565',
            verticalAlign: 'baseline',
            p: 0,
            border: 'none',
            background: 'none',
            fontFamily: 'inherit',
          }}
        >
          Contact your administrator.
        </Link>
      </Typography>
    </Box>
  );
}
